import { useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type SortingState,
  type VisibilityState,
} from "@tanstack/react-table";
import type { TaskRow, CustomFieldRow } from "@shared/types";
import { Icon } from "./Icon";
import { Pill } from "./Pill";
import { PriorityChip } from "./PriorityChip";
import { Avatar } from "./Avatar";
import { TASK_STATUS_LABEL, taskStatusOrder } from "@/lib/taskStatus";
import { priorityLabel } from "@/lib/taskPriority";
import { typeLabel, typeMeta, WORK_ITEM_TYPES } from "@/lib/workItemType";
import { useEffortUnit, formatEffort } from "@/lib/effort";
import { formatDateShort, classifyDue } from "@/lib/format";
import {
  type TaskFilters,
  type SavedView,
  EMPTY_FILTERS,
  loadSavedViews,
  persistSavedViews,
  toCsv,
  downloadCsv,
} from "@/lib/taskTableViews";

type Users = ReadonlyMap<string, { name: string; image: string | null }>;
type CfValue = string | number | boolean | string[] | null;
type CfValueRef = { fieldId: string; taskId: string; value: CfValue };

const col = createColumnHelper<TaskRow>();

/** Render a custom-field value as display text. */
function formatCfValue(v: CfValue): string {
  if (v == null || v === "") return "";
  if (Array.isArray(v)) return v.join(", ");
  if (typeof v === "boolean") return v ? "✓" : "";
  return String(v);
}

const STATUS_RANK: Record<string, number> = Object.fromEntries(
  taskStatusOrder.map((s, i) => [s, i]),
);
const TYPE_RANK: Record<string, number> = Object.fromEntries(
  WORK_ITEM_TYPES.map((t, i) => [t, i]),
);

const DEFAULT_ORDER = [
  "title",
  "type",
  "status",
  "priority",
  "assignee",
  "startDate",
  "dueDate",
  "estimate",
];

const COLUMN_LABELS: Record<string, string> = {
  title: "Title",
  type: "Type",
  status: "Status",
  priority: "Priority",
  assignee: "Assignee",
  startDate: "Start",
  dueDate: "Due",
  estimate: "Estimate",
};

export function TaskTable({
  projectId,
  tasks,
  users,
  fields = [],
  values = [],
}: {
  projectId: string;
  tasks: TaskRow[];
  users: Users;
  fields?: CustomFieldRow[];
  values?: CfValueRef[];
}) {
  const effortUnit = useEffortUnit();
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const cfIds = useMemo(() => fields.map((f) => `cf_${f.id}`), [fields]);
  const [columnOrder, setColumnOrder] = useState<string[]>([
    ...DEFAULT_ORDER,
    ...cfIds,
  ]);
  // Keep newly-added custom-field columns appended to the order.
  const effectiveOrder = useMemo(() => {
    const seen = new Set(columnOrder);
    return [...columnOrder, ...cfIds.filter((id) => !seen.has(id))];
  }, [columnOrder, cfIds]);
  const [filters, setFilters] = useState<TaskFilters>(EMPTY_FILTERS);
  const [cfFilters, setCfFilters] = useState<Record<string, string>>({});

  // taskId -> fieldId -> value
  const valueMap = useMemo(() => {
    const m = new Map<string, Map<string, CfValue>>();
    for (const v of values) {
      let inner = m.get(v.taskId);
      if (!inner) m.set(v.taskId, (inner = new Map()));
      inner.set(v.fieldId, v.value);
    }
    return m;
  }, [values]);
  const cfValue = (taskId: string, fieldId: string): CfValue =>
    valueMap.get(taskId)?.get(fieldId) ?? null;
  const cfLabel = (id: string) =>
    fields.find((f) => `cf_${f.id}` === id)?.name ?? id;

  const [openPanel, setOpenPanel] = useState<"columns" | "filter" | null>(null);
  const [savedViews, setSavedViews] = useState<SavedView[]>(() =>
    loadSavedViews(projectId),
  );

  const nameOf = (id: string | null | undefined) =>
    id ? (users.get(id)?.name ?? id.slice(0, 8)) : "";

  // Pre-filter rows (combined AND) so sort + CSV operate on the visible set.
  const filtered = useMemo(() => {
    return tasks.filter((t) => {
      if (filters.status.length && !filters.status.includes(t.status))
        return false;
      if (filters.type.length && !filters.type.includes(t.type ?? "task"))
        return false;
      if (filters.priority.length && !filters.priority.includes(t.priority))
        return false;
      if (filters.assignee) {
        const ids = t.assigneeIds?.length
          ? t.assigneeIds
          : t.assigneeId
            ? [t.assigneeId]
            : [];
        if (filters.assignee === "__none__") {
          if (ids.length) return false;
        } else if (!ids.includes(filters.assignee)) {
          return false;
        }
      }
      // Custom-field filters: case-insensitive substring on the display text.
      for (const [fieldId, q] of Object.entries(cfFilters)) {
        if (!q.trim()) continue;
        const text = formatCfValue(cfValue(t.id, fieldId)).toLowerCase();
        if (!text.includes(q.trim().toLowerCase())) return false;
      }
      return true;
    });
  }, [tasks, filters, cfFilters, valueMap]);

  const columns = useMemo(
    () => [
      col.accessor("title", {
        id: "title",
        header: "Title",
        cell: (c) => (
          <Link
            to="/tasks/$taskId"
            params={{ taskId: c.row.original.id }}
            className="tt-title"
          >
            {c.getValue()}
          </Link>
        ),
      }),
      col.accessor((t) => t.type ?? "task", {
        id: "type",
        header: "Type",
        sortingFn: (a, b) =>
          (TYPE_RANK[a.original.type ?? "task"] ?? 0) -
          (TYPE_RANK[b.original.type ?? "task"] ?? 0),
        cell: (c) => {
          const ty = c.row.original.type ?? "task";
          return (
            <span style={{ color: typeMeta(ty).color }}>
              <Icon name={typeMeta(ty).icon} size={12} /> {typeLabel(ty)}
            </span>
          );
        },
      }),
      col.accessor("status", {
        id: "status",
        header: "Status",
        sortingFn: (a, b) =>
          (STATUS_RANK[a.original.status] ?? 0) -
          (STATUS_RANK[b.original.status] ?? 0),
        cell: (c) => (
          <Pill variant={c.getValue()} dot>
            {TASK_STATUS_LABEL[c.getValue()]}
          </Pill>
        ),
      }),
      col.accessor("priority", {
        id: "priority",
        header: "Priority",
        cell: (c) => <PriorityChip level={c.getValue()} />,
      }),
      col.accessor((t) => nameOf(t.assigneeIds?.[0] ?? t.assigneeId), {
        id: "assignee",
        header: "Assignee",
        cell: (c) => {
          const id =
            c.row.original.assigneeIds?.[0] ?? c.row.original.assigneeId;
          if (!id) return <span className="t-muted">—</span>;
          return (
            <span className="tt-assignee">
              <Avatar
                id={id}
                name={nameOf(id)}
                size={20}
                image={users.get(id)?.image ?? null}
              />
              <span>{nameOf(id)}</span>
            </span>
          );
        },
      }),
      col.accessor((t) => t.startDate ?? "", {
        id: "startDate",
        header: "Start",
        cell: (c) => {
          const v = c.row.original.startDate;
          return v ? (
            <span className="t-muted">{formatDateShort(v)}</span>
          ) : (
            <span className="t-muted">—</span>
          );
        },
      }),
      col.accessor((t) => t.dueDate ?? "", {
        id: "dueDate",
        header: "Due",
        cell: (c) => {
          const v = c.row.original.dueDate;
          if (!v) return <span className="t-muted">—</span>;
          const cls = classifyDue(v);
          return (
            <span className={cls === "overdue" ? "t-danger" : undefined}>
              {formatDateShort(v)}
            </span>
          );
        },
      }),
      col.accessor((t) => t.estimate ?? -1, {
        id: "estimate",
        header: "Estimate",
        cell: (c) => {
          const label = formatEffort(c.row.original.estimate, effortUnit);
          return label ? (
            <span>{label}</span>
          ) : (
            <span className="t-muted">—</span>
          );
        },
      }),
    ],
    [users, effortUnit],
  );

  // Dynamic custom-field columns. Number fields sort numerically; others by
  // their display text.
  const cfColumns = useMemo(
    () =>
      fields.map((f) =>
        col.accessor(
          (t) => {
            const v = cfValue(t.id, f.id);
            if (f.type === "number")
              return typeof v === "number" ? v : -Infinity;
            return formatCfValue(v).toLowerCase();
          },
          {
            id: `cf_${f.id}`,
            header: f.name,
            cell: (c) => {
              const text = formatCfValue(cfValue(c.row.original.id, f.id));
              return text ? (
                <span>{text}</span>
              ) : (
                <span className="t-muted">—</span>
              );
            },
          },
        ),
      ),
    [fields, valueMap],
  );
  const allColumns = useMemo(
    () => [...columns, ...cfColumns],
    [columns, cfColumns],
  );

  const table = useReactTable({
    data: filtered,
    columns: allColumns,
    state: { sorting, columnVisibility, columnOrder: effectiveOrder },
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnOrderChange: setColumnOrder,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  // ---- Saved views ----
  function saveCurrentView() {
    const name = window.prompt("Name this view");
    if (!name?.trim()) return;
    const next = [
      ...savedViews.filter((v) => v.name !== name.trim()),
      {
        name: name.trim(),
        state: { sorting, columnVisibility, columnOrder, filters },
      },
    ];
    setSavedViews(next);
    persistSavedViews(projectId, next);
  }
  function applyView(name: string) {
    const v = savedViews.find((x) => x.name === name);
    if (!v) return;
    setSorting(v.state.sorting ?? []);
    setColumnVisibility(v.state.columnVisibility ?? {});
    setColumnOrder(v.state.columnOrder ?? DEFAULT_ORDER);
    setFilters(v.state.filters ?? EMPTY_FILTERS);
  }
  function deleteView(name: string) {
    const next = savedViews.filter((v) => v.name !== name);
    setSavedViews(next);
    persistSavedViews(projectId, next);
  }

  // ---- CSV export of the current (filtered + sorted + visible) rows ----
  function exportCsv() {
    const visible = table.getVisibleLeafColumns();
    const headers = visible.map((c) => COLUMN_LABELS[c.id] ?? cfLabel(c.id));
    const rows = table
      .getSortedRowModel()
      .rows.map((r) =>
        visible.map((c) =>
          c.id.startsWith("cf_")
            ? formatCfValue(cfValue(r.original.id, c.id.slice(3)))
            : csvValue(c.id, r.original, nameOf, effortUnit),
        ),
      );
    downloadCsv(`tasks-${projectId}.csv`, toCsv(headers, rows));
  }

  function moveColumn(id: string, dir: -1 | 1) {
    setColumnOrder((order) => {
      const ids = order.length ? [...order] : [...DEFAULT_ORDER];
      const i = ids.indexOf(id);
      const j = i + dir;
      if (i < 0 || j < 0 || j >= ids.length) return ids;
      const a = ids[i];
      const b = ids[j];
      if (a === undefined || b === undefined) return ids;
      ids[i] = b;
      ids[j] = a;
      return ids;
    });
  }

  const activeFilterCount =
    filters.status.length +
    filters.type.length +
    filters.priority.length +
    (filters.assignee ? 1 : 0);

  return (
    <div className="task-table-wrap">
      <div className="tt-toolbar">
        <button
          type="button"
          className={`chip-btn${openPanel === "columns" ? " on" : ""}`}
          onClick={() =>
            setOpenPanel((p) => (p === "columns" ? null : "columns"))
          }
        >
          <Icon name="grid" size={14} /> Columns
        </button>
        <button
          type="button"
          className={`chip-btn${openPanel === "filter" || activeFilterCount ? " on" : ""}`}
          onClick={() =>
            setOpenPanel((p) => (p === "filter" ? null : "filter"))
          }
        >
          <Icon name="filter" size={14} /> Filter
          {activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
        </button>
        <select
          className="select"
          style={{ width: 160 }}
          value=""
          onChange={(e) => {
            if (e.target.value) applyView(e.target.value);
          }}
          aria-label="Open saved view"
        >
          <option value="">Saved views…</option>
          {savedViews.map((v) => (
            <option key={v.name} value={v.name}>
              {v.name}
            </option>
          ))}
        </select>
        <button type="button" className="chip-btn" onClick={saveCurrentView}>
          <Icon name="pin" size={14} /> Save view
        </button>
        <span style={{ flex: 1 }} />
        <button type="button" className="chip-btn" onClick={exportCsv}>
          <Icon name="clip" size={14} /> Export CSV
        </button>
      </div>

      {openPanel === "columns" && (
        <div className="tt-panel">
          {effectiveOrder.map((id) => {
            const c = table.getColumn(id);
            if (!c) return null;
            const label = COLUMN_LABELS[id] ?? cfLabel(id);
            return (
              <div key={id} className="tt-col-row">
                <label className="tt-col-label">
                  <input
                    type="checkbox"
                    checked={c.getIsVisible()}
                    onChange={c.getToggleVisibilityHandler()}
                  />
                  {label}
                </label>
                <span className="tt-col-actions">
                  <button
                    type="button"
                    className="icon-btn"
                    aria-label={`Move ${label} left`}
                    onClick={() => moveColumn(id, -1)}
                  >
                    <Icon name="arrow-u" size={12} />
                  </button>
                  <button
                    type="button"
                    className="icon-btn"
                    aria-label={`Move ${label} right`}
                    onClick={() => moveColumn(id, 1)}
                  >
                    <Icon name="arrow-d" size={12} />
                  </button>
                </span>
              </div>
            );
          })}
        </div>
      )}

      {openPanel === "filter" && (
        <div className="tt-panel tt-filter-panel">
          <FilterGroup
            label="Status"
            options={taskStatusOrder.map((s) => ({
              value: s,
              label: TASK_STATUS_LABEL[s],
            }))}
            selected={filters.status}
            onToggle={(v) =>
              setFilters((f) => ({ ...f, status: toggle(f.status, v) }))
            }
          />
          <FilterGroup
            label="Type"
            options={WORK_ITEM_TYPES.map((t) => ({
              value: t,
              label: typeLabel(t),
            }))}
            selected={filters.type}
            onToggle={(v) =>
              setFilters((f) => ({ ...f, type: toggle(f.type, v) }))
            }
          />
          <FilterGroup
            label="Priority"
            options={[1, 2, 3, 4].map((p) => ({
              value: String(p),
              label: priorityLabel(p),
            }))}
            selected={filters.priority.map(String)}
            onToggle={(v) =>
              setFilters((f) => ({
                ...f,
                priority: toggleNum(f.priority, Number(v)),
              }))
            }
          />
          <div className="tt-filter-grp">
            <div className="tt-filter-head">Assignee</div>
            <select
              className="select"
              value={filters.assignee}
              onChange={(e) =>
                setFilters((f) => ({ ...f, assignee: e.target.value }))
              }
            >
              <option value="">Anyone</option>
              <option value="__none__">Unassigned</option>
              {[...users.entries()].map(([id, u]) => (
                <option key={id} value={id}>
                  {u.name}
                </option>
              ))}
            </select>
          </div>
          {fields.map((f) => (
            <div className="tt-filter-grp" key={f.id}>
              <div className="tt-filter-head">{f.name}</div>
              <input
                className="input"
                placeholder="contains…"
                value={cfFilters[f.id] ?? ""}
                onChange={(e) =>
                  setCfFilters((s) => ({ ...s, [f.id]: e.target.value }))
                }
                aria-label={`Filter by ${f.name}`}
              />
            </div>
          ))}
          {(activeFilterCount > 0 ||
            savedViews.length > 0 ||
            Object.values(cfFilters).some((v) => v.trim())) && (
            <button
              type="button"
              className="chip-btn"
              onClick={() => {
                setFilters(EMPTY_FILTERS);
                setCfFilters({});
              }}
            >
              Clear filters
            </button>
          )}
        </div>
      )}

      <div className="files-table-wrap">
        <table className="files-table task-table">
          <thead>
            {table.getHeaderGroups().map((hg) => (
              <tr key={hg.id}>
                {hg.headers.map((h) => {
                  const sortDir = h.column.getIsSorted();
                  return (
                    <th
                      key={h.id}
                      className={`ft-th ft-th-${h.column.id}`}
                      onClick={h.column.getToggleSortingHandler()}
                      style={{
                        cursor: h.column.getCanSort() ? "pointer" : "default",
                      }}
                    >
                      {flexRender(h.column.columnDef.header, h.getContext())}
                      {sortDir ? (sortDir === "asc" ? " ▲" : " ▼") : ""}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id} className="ft-row">
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className={`ft-td ft-td-${cell.column.id}`}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <p className="t-sm t-muted" style={{ padding: "16px 4px" }}>
            No tasks match the current filters.
          </p>
        )}
      </div>

      {savedViews.length > 0 && (
        <div className="tt-saved-list t-sm t-muted">
          Saved views:{" "}
          {savedViews.map((v) => (
            <span key={v.name} className="tt-saved-chip">
              <button
                type="button"
                className="link-btn"
                onClick={() => applyView(v.name)}
              >
                {v.name}
              </button>
              <button
                type="button"
                className="link-btn"
                aria-label={`Delete view ${v.name}`}
                onClick={() => deleteView(v.name)}
              >
                ✕
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function FilterGroup({
  label,
  options,
  selected,
  onToggle,
}: {
  label: string;
  options: { value: string; label: string }[];
  selected: string[];
  onToggle: (v: string) => void;
}) {
  return (
    <div className="tt-filter-grp">
      <div className="tt-filter-head">{label}</div>
      {options.map((o) => (
        <label key={o.value} className="tt-col-label">
          <input
            type="checkbox"
            checked={selected.includes(o.value)}
            onChange={() => onToggle(o.value)}
          />
          {o.label}
        </label>
      ))}
    </div>
  );
}

function toggle(arr: string[], v: string): string[] {
  return arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v];
}
function toggleNum(arr: number[], v: number): number[] {
  return arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v];
}

function csvValue(
  colId: string,
  t: TaskRow,
  nameOf: (id: string | null | undefined) => string,
  effortUnit: "points" | "hours",
): string {
  switch (colId) {
    case "title":
      return t.title;
    case "type":
      return typeLabel(t.type ?? "task");
    case "status":
      return TASK_STATUS_LABEL[t.status];
    case "priority":
      return priorityLabel(t.priority);
    case "assignee":
      return nameOf(t.assigneeIds?.[0] ?? t.assigneeId);
    case "startDate":
      return t.startDate ?? "";
    case "dueDate":
      return t.dueDate ?? "";
    case "estimate":
      return formatEffort(t.estimate, effortUnit) ?? "";
    default:
      return "";
  }
}
